import React, { useEffect, useRef, useState } from 'react';
import { ExpandMore, ExpandLess } from '@mui/icons-material';

// Declare anime as global from CDN
declare global {
  interface Window {
    anime: any;
  }
}

interface ScoreData {
  label: string;
  score: number; // 0-100
  description?: string;
}

interface RadarChartProps {
  data: ScoreData[];
}

const RadarChart: React.FC<RadarChartProps> = ({ data }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  
  const scrollToDescription = (index: number) => {
    // Scroll to the detailed content section
    const contentElement = document.getElementById('puanlama-detailed-content');
    if (contentElement) {
      // Get the offset to account for any sticky headers
      const headerOffset = 100;
      const elementPosition = contentElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      
      // Add a brief highlight to the entire content area
      const originalBg = contentElement.style.backgroundColor;
      contentElement.style.transition = 'background-color 2s ease';
      contentElement.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
      setTimeout(() => {
        contentElement.style.backgroundColor = originalBg || '';
      }, 2000);
    }
  };

  const toggleExpand = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  useEffect(() => {
    if (!svgRef.current) {
      console.log('RadarChart: svgRef not available');
      return;
    }
    
    if (data.length === 0) {
      console.log('RadarChart: No data to render');
      // Clear SVG if no data
      if (svgRef.current) {
        svgRef.current.innerHTML = '';
      }
      return;
    }

    console.log('RadarChart: Starting render with', data.length, 'data points');

    // Define renderChart function first (before it's used)
    const renderChart = (animeLib: any) => {
      if (!svgRef.current) {
        console.error('RadarChart: svgRef.current is null');
        return;
      }

      console.log('RadarChart: renderChart called with', data.length, 'data points');
      console.log('RadarChart: Data:', data);

      const svgEl = svgRef.current;

      // Clear previous content
      svgEl.innerHTML = '';

      const svgSize = 500;
      const center = svgSize / 2;
      const radius = 140;
      const totalPoints = data.length;
      const angleStep = (Math.PI * 2) / totalPoints;

      console.log('RadarChart: Chart parameters - center:', center, 'radius:', radius, 'totalPoints:', totalPoints, 'angleStep:', angleStep);

      // Helper function to get coordinates
      const getCoordinates = (index: number, score: number) => {
        const angle = (index * angleStep) - (Math.PI / 2);
        const r = (score / 100) * radius;
        const x = center + r * Math.cos(angle);
        const y = center + r * Math.sin(angle);
        return { x, y, angle };
      };

      let gridPoints = '';
      let zeroPoints = '';
      let finalPoints = '';

      data.forEach((item, i) => {
        // Ensure score is in valid range (0-100)
        const normalizedScore = Math.max(0, Math.min(100, item.score));
        
        // Background grid (full size)
        const fullCoords = getCoordinates(i, 100);
        gridPoints += `${fullCoords.x},${fullCoords.y} `;

        // Data polygon coordinates
        const startCoords = getCoordinates(i, 0);
        const endCoords = getCoordinates(i, normalizedScore);

        zeroPoints += `${startCoords.x},${startCoords.y} `;
        finalPoints += `${endCoords.x},${endCoords.y} `;

        // Axis lines
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', center.toString());
        line.setAttribute('y1', center.toString());
        line.setAttribute('x2', fullCoords.x.toString());
        line.setAttribute('y2', fullCoords.y.toString());
        line.setAttribute('class', 'axis-line');
        svgEl.appendChild(line);

        // Labels - placed first, at outer edge (closer to center)
        let labelDistance = 160; // Default
        if (i === 0) labelDistance = 130;
        else if (i === 2) labelDistance = 160;
        else if (i === 4) labelDistance = 150;
        else if ([1, 3, 5, 6, 7].includes(i)) labelDistance = 140;
        const labelCoords = getCoordinates(i, labelDistance);

        // Score numbers - placed above labels, centered on same x
        // Use same x as label for perfect alignment, but adjust y to be above
        const scoreY = labelCoords.y - 25; // Position score 25 pixels above label center (reduced from 30)
        
        // Score numbers (will be animated) - placed above labels, centered
        const scoreText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        scoreText.setAttribute('x', labelCoords.x.toString());
        scoreText.setAttribute('y', scoreY.toString());
        scoreText.setAttribute('class', 'score-text');
        scoreText.setAttribute('text-anchor', 'middle');
        scoreText.setAttribute('dominant-baseline', 'middle');
        scoreText.setAttribute('id', `score-${i}`);
        scoreText.textContent = '0';
        svgEl.appendChild(scoreText);

        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.setAttribute('x', labelCoords.x.toString());
        text.setAttribute('y', labelCoords.y.toString());
        text.setAttribute('class', 'label-text');
        text.setAttribute('text-anchor', 'middle');
        text.setAttribute('dominant-baseline', 'middle');
        
        // Split long labels into two lines intelligently
        const maxCharsPerLine = 16;
        let line1 = '';
        let line2 = '';
        
        if (item.label.length <= maxCharsPerLine) {
          // Single line
          text.textContent = item.label;
        } else {
          // Two lines - split at word boundaries
          const words = item.label.split(' ');
          let currentLine = '';
          const lines: string[] = [];
          
          for (const word of words) {
            if ((currentLine + ' ' + word).length <= maxCharsPerLine && currentLine.length > 0) {
              currentLine += ' ' + word;
            } else {
              if (currentLine.length > 0) {
                lines.push(currentLine);
              }
              currentLine = word;
            }
          }
          if (currentLine.length > 0) {
            lines.push(currentLine);
          }
          
          line1 = lines[0] || '';
          line2 = lines.slice(1).join(' ') || '';
          
          // Create first line
          const tspan1 = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
          tspan1.setAttribute('x', labelCoords.x.toString());
          tspan1.setAttribute('dy', '-0.6em');
          tspan1.setAttribute('text-anchor', 'middle');
          tspan1.textContent = line1;
          text.appendChild(tspan1);
          
          // Create second line (only if we have content)
          if (line2) {
            const tspan2 = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
            tspan2.setAttribute('x', labelCoords.x.toString());
            tspan2.setAttribute('dy', '1.2em');
            tspan2.setAttribute('text-anchor', 'middle');
            tspan2.textContent = line2;
            text.appendChild(tspan2);
          }
        }
        
        svgEl.appendChild(text);
      });

      // Background grid
      const gridPoly = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
      gridPoly.setAttribute('points', gridPoints);
      gridPoly.setAttribute('class', 'grid-shape');
      svgEl.prepend(gridPoly);

      // Data polygon (starts from center)
      const dataPoly = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
      dataPoly.setAttribute('points', zeroPoints);
      dataPoly.setAttribute('class', 'data-shape');
      dataPoly.setAttribute('id', 'poly-shape');
      svgEl.appendChild(dataPoly);

      // Animations
      // 1. Polygon expansion
      console.log('RadarChart: Starting polygon animation');
      console.log('RadarChart: zeroPoints:', zeroPoints);
      console.log('RadarChart: finalPoints:', finalPoints);
      
      try {
        animeLib({
          targets: '#poly-shape',
          points: [
            { value: zeroPoints },
            { value: finalPoints }
          ],
          easing: 'spring(1, 80, 10, 0)',
          duration: 1500,
          loop: false
        });
        console.log('RadarChart: Polygon animation started');
      } catch (err) {
        console.error('RadarChart: Error in polygon animation:', err);
        // Fallback: set points directly without animation
        const dataPoly = document.getElementById('poly-shape') as unknown as SVGPolygonElement;
        if (dataPoly) {
          dataPoly.setAttribute('points', finalPoints);
          console.log('RadarChart: Set polygon points directly (no animation)');
        }
      }

      // 2. Score numbers count up
      console.log('RadarChart: Starting score number animations');
      data.forEach((item, i) => {
        // Score is already in 0-100 scale, convert to 0-10 for display
        const scoreValue = Math.round(item.score / 10);
        console.log(`RadarChart: Animating score ${i}: ${scoreValue}/10 (from ${item.score}/100)`);
        const obj = { score: 0 };
        try {
          animeLib({
            targets: obj,
            score: scoreValue,
            round: 1,
            easing: 'linear',
            duration: 1000,
            delay: 200, // Small delay after polygon animation
            update: function() {
              const scoreEl = document.getElementById(`score-${i}`);
              if (scoreEl) {
                scoreEl.textContent = Math.round(obj.score).toString();
              }
            }
          });
        } catch (err) {
          console.error(`RadarChart: Error animating score ${i}:`, err);
          // Fallback: set score directly
          const scoreEl = document.getElementById(`score-${i}`);
          if (scoreEl) {
            scoreEl.textContent = scoreValue.toString();
          }
        }
      });

      console.log('RadarChart: Render complete');
    };

    // Load anime.js from CDN if not already loaded
    const loadAnime = () => {
      if (typeof window !== 'undefined' && window.anime) {
        console.log('RadarChart: anime.js already loaded');
        renderChart(window.anime);
        return;
      }

      // Check if script is already being loaded
      const existingScript = document.querySelector('script[src*="anime"]');
      if (existingScript) {
        console.log('RadarChart: anime.js script already exists, waiting...');
        const checkAnime = () => {
          if (window.anime) {
            console.log('RadarChart: anime.js loaded after waiting');
            renderChart(window.anime);
          } else {
            setTimeout(checkAnime, 100);
          }
        };
        checkAnime();
        return;
      }

      // Load anime.js from CDN
      console.log('RadarChart: Loading anime.js from CDN');
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/animejs/3.2.1/anime.min.js';
      script.onload = () => {
        console.log('RadarChart: anime.js loaded successfully');
        if (window.anime) {
          renderChart(window.anime);
        }
      };
      script.onerror = () => {
        console.error('RadarChart: Failed to load anime.js');
      };
      document.head.appendChild(script);
    };

    // Wait for anime.js to load from CDN
    const checkAnime = () => {
      if (typeof window !== 'undefined' && window.anime) {
        const animeLib = window.anime;
        renderChart(animeLib);
      } else {
        // Retry after a short delay if anime.js hasn't loaded yet
        setTimeout(checkAnime, 100);
      }
    };

    loadAnime();
  }, [data]);

  return (
    <div className="flex flex-col lg:flex-row gap-8 lg:gap-10 items-center lg:items-start justify-center lg:justify-start w-full">
      <div className="chart-wrapper flex-shrink-0 w-full lg:w-auto">
        <svg 
          ref={svgRef}
          viewBox="0 0 500 500" 
          className="w-full h-full max-w-[500px] max-h-[500px] mx-auto"
          style={{ overflow: 'visible' }}
        />
      </div>
      <div className="stats-list flex-1 min-w-0 lg:min-w-[320px] max-w-full lg:max-w-[420px] w-full lg:w-auto">
        {data.map((item, index) => {
          const scoreValue = Math.round(item.score / 10);
          const isExpanded = expandedItems.has(index);
          
          return (
            <div key={index} className="stat-item-container mb-3 w-full">
              <div 
                className="stat-item cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={() => scrollToDescription(index)}
              >
                <span className="stat-name flex-1 pr-4">{item.label}</span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {item.description && (
                    <span className="text-gray-400 dark:text-gray-500">
                      {isExpanded ? (
                        <ExpandLess style={{ width: '20px', height: '20px', display: 'block' }} />
                      ) : (
                        <ExpandMore style={{ width: '20px', height: '20px', display: 'block' }} />
                      )}
                    </span>
                  )}
                  <span className={`stat-value ${scoreValue > 7 ? 'high' : ''}`}>{scoreValue}</span>
                </div>
              </div>
              {isExpanded && item.description && (
                <div className="stat-description">
                  <div className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-wrap">
                    {item.description}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RadarChart;

